import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { RadioButton } from "primereact/radiobutton";
import { Chart } from "primereact/chart";
import { useSelector, useDispatch } from 'react-redux';
import * as jobConstants from "../../enums/jobs";
import moment from "moment";

import {
  setFilterReportType,
  fetchSlaReportData,
} from "./reportSlice";

const MIN_BAR_WIDTH = 55;

const getMonthYearGroups = (slaGraphData) => {
  const groups = [];
  slaGraphData.forEach((item, index) => {
    const date = new Date(item.MonthYear);
    const key = `${date.getFullYear()}-${date.getMonth()}`;
    const label = date.toLocaleString("en-US", { month: "short", year: "2-digit" }).replace(" ", "-");
    if (groups.length === 0 || groups[groups.length - 1].key !== key) {
      groups.push({ key, label, startIndex: index, count: 1 });
    } else {
      groups[groups.length - 1].count += 1;
    }
  });
  return groups;
};

export const SlaExpendedView = () => {
  const navigate = useNavigate();
  const [chartData, setChartData] = useState({});
  const [chartOptions, setChartOptions] = useState({});
  const [monthGroups, setMonthGroups] = useState([]);
  const [chartWidth, setChartWidth] = useState("100%");
  const stackedBarTopLabels = {
    id: 'stackedBarTopLabels',
    afterDatasetsDraw(chart) {
      const { ctx, data, scales: { x, y } } = chart;
      const metData = data.datasets[0]?.data || [];
      const breachData = data.datasets[1]?.data || [];
      ctx.save();
      ctx.font = 'bold 11px sans-serif';
      ctx.fillStyle = '#333';
      ctx.textAlign = 'center';
      metData.forEach((met, i) => {
        const breach = breachData[i] || 0;
        const total = (met || 0) + breach;
        const xPos = x.getPixelForValue(i);
        const yPos = y.getPixelForValue(total) - 6;
        ctx.fillText(`M-${met} / B-${breach}`, xPos, yPos);
      });
      ctx.restore();
    },
  };
  const protrackReports = useSelector((state) => state.reports);
  const jobState = useSelector((state) => state.job);
  const {
    userId,
    clientId,
    appLanguage,
  } = jobState;
  const {
    slaReportList,
    slaMetBreachDownloadReportList,
    reportStartDate,
    reportEndDate,
    filterChannelType,
    filterDocType,
    tabViewType,
    filterReportType,
  } = protrackReports;
  const categories = jobConstants.REPORT_FILTER_TYPE;
  const dispatch = useDispatch();

  useEffect(() => {
    const slaGraphData = slaReportList?.slamvsb ? slaReportList?.slamvsb : [];

    const getLabel = (monthYear) => {
      const date = new Date(monthYear);
      if (filterReportType === 1) {
        const mon = date.toLocaleString("en-US", { month: "short" });
        const yr = String(date.getFullYear()).slice(-2);
        return `${mon}-${yr}`;
      }
      if (filterReportType === 2) return date.getFullYear().toString();
      return date.getDate().toString();
    };

    const documentStyle = getComputedStyle(document.documentElement);
    const textColorSecondary = documentStyle.getPropertyValue("--text-color-secondary");
    const surfaceBorder = documentStyle.getPropertyValue("--surface-border");

    const data = {
      labels: slaGraphData.map(item => getLabel(item.MonthYear)),
      datasets: [
        {
          type: "bar",
          label: appLanguage["report.sla.label.sla-meet"],
          backgroundColor: '#7F7F7F',
          data: slaGraphData.map(item => item.totalcommsSLAMet),
        },
        {
          type: "bar",
          label: appLanguage["report.sla.label.total-sla-fail"],
          backgroundColor: '#C00000',
          data: slaGraphData.map(item => item.totalcommsSLABreach),
        },
      ],
    };

    const options = {
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
      },
      scales: {
        x: {
          stacked: true,
          ticks: { color: textColorSecondary },
          grid: { color: surfaceBorder, display: false },
        },
        y: {
          stacked: true,
          ticks: { color: textColorSecondary },
          grid: { color: surfaceBorder },
        },
        afterDataLimits(scale) {
          scale.max = scale.max * 1.15;
        },
      },
    };

    const groups = filterReportType === 0 ? getMonthYearGroups(slaGraphData) : [];
    const totalBars = slaGraphData.length;
    const computedWidth = Math.max(totalBars * MIN_BAR_WIDTH, 1103);

    setChartData(data);
    setChartOptions(options);
    setMonthGroups(groups);
    setChartWidth(computedWidth);
  }, [slaReportList, appLanguage, filterReportType]);

  const changeReportType = (e) => {
    dispatch(setFilterReportType(e.value));
    let filterParams = {
      clientId: clientId,
      userId: userId,
      typeId: tabViewType,
      channelType: filterChannelType.join("|"),
      filterType: filterDocType,
      fromDate: moment(reportStartDate).format("YYYY-MM-DD"),
      toDate: moment(reportEndDate).format("YYYY-MM-DD"),
      reportType: e.value,
    };
    dispatch(fetchSlaReportData(filterParams));
  };

  const totalBars = chartData?.labels?.length || 0;

  const downloadSlaMetBreachReport = () => {
    downloadCsv(slaMetBreachDownloadReportList, 'sla_met_breach_report.csv');
  }

  // Convert JSON to CSV
  const jsonToCsv = (jsonData) => {
    const isArray = Array.isArray(jsonData);
    if (isArray) {
      const headers = Object.keys(jsonData[0]);
      const csvRows = [];
      csvRows.push(headers.join(','));
      jsonData.forEach(item => {
        const values = headers.map(header => {
          const value = item[header];
          return `"${value !== null && value !== undefined ? value.toString().replace(/"/g, '""') : ''}"`;
        });
        csvRows.push(values.join(','));
      });
      return csvRows.join('\n');
    } else {
      const headers = Object.keys(jsonData);
      const values = headers.map(header => {
        const value = jsonData[header];
        return `"${value !== null && value !== undefined ? value.toString().replace(/"/g, '""') : ''}"`;
      });
      return `${headers.join(',')}\n${values.join(',')}`;
    }
  };

  // Trigger CSV download
  const downloadCsv = (jsonData, fileName) => {
    const csvData = jsonToCsv(jsonData);
    console.log('CSV Data:', csvData);
    const blob = new Blob([csvData], { type: 'text/csv' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    link.click();
  };

  return (
    <div className="comms-sla-met-wrapper">
      <div className="comms-sla-met-inner-wrap">
        <div className="left-col box-white">
          <h3 style={{ marginLeft: '100px' }}>
            {appLanguage["report.sla.label.sla-meet-vs-sla-breach"]}
            <ul className="comms-sla-radio-box">
              {categories?.map((category) => (
                <li key={category.key}>
                  <label htmlFor={category.key}>{category.name}</label>
                  <RadioButton
                    inputId={category.key}
                    name={category.key}
                    value={category.key}
                    onChange={(e) => changeReportType(e)}
                    checked={filterReportType === category.key}
                  />
                </li>
              ))}
            </ul>
          </h3>
          <div className="card" style={{ width: chartWidth, minWidth: chartWidth }}>
            <Chart
              type="bar"
              data={chartData}
              options={chartOptions}
              plugins={[stackedBarTopLabels]}
              height={470}
            />
            <div className="sla-month-axis" style={{ display: 'flex', paddingLeft: '40px' }}>
              {monthGroups.map((group) => (
                <div
                  key={group.key}
                  style={{
                    width: `${(group.count / totalBars) * 100}%`,
                    textAlign: 'center',
                    fontSize: '12px',
                    color: '#7F7F7F',
                    borderLeft: '1px solid #BFBFBF',
                    paddingTop: '4px',
                    fontWeight: '600',
                  }}>
                  {group.label}
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="right-col">
          <div className="applied-filter-box">
            <p className="title">Applied Filters</p>
            <p style={{ width: '100px' }}>{moment(reportStartDate).format("DD MMM YYYY")} – {moment(reportEndDate).format("DD MMM YYYY")}</p>
            <p>{filterChannelType == 0 ? 'DOCUMENT' : filterChannelType == 1 ? 'EMAIL' : filterChannelType == 2 ? 'SMS' : 'ALL'}</p>
            <p>{filterDocType === 1 ? 'BATCH' : filterDocType === 2 ? 'ADHOC' : 'ALL'}</p>
          </div>
          <div className="comms-btn-wrapper">
            <button className="return-icon-comms" onClick={() => navigate("/reports")}>Return</button>
            <button className="download-icon-comms" onClick={downloadSlaMetBreachReport}>Download</button>
          </div>
        </div>
      </div>
    </div>
  );
};
